// ---- UI ------------------------------------------------------------------
// React components, authored in JSX (compiled by esbuild). All tournament math
// lives in the pure modules; this file is presentation + local interaction.

import { useState, useMemo, useEffect, useRef } from "react";

import { GROUPS, GROUP_KEYS, FLAGS, ELO } from "./data.js";
import {
  expScore,
  simulateGroupProbs,
  computeEloForecast
} from "./forecast.js";
import { assignThirds, buildBracket, pruneDownstream } from "./bracket.js";
import { groupClickAction } from "./interaction.js";
import { saveState, clearSaved } from "./storage.js";
import { buildHash } from "./hash.js";

// ---- palette ----
const C = {
  bg: "#0a1628",
  panel: "#0f1f38",
  panel2: "#13284a",
  line: "#1d3a5f",
  gold: "#f5c542",
  goldDim: "#9a7c1f",
  text: "#e8eef7",
  dim: "#7e93b0",
  green: "#34d399"
};

// ---- two-sided bracket layout: R32-index halves -------------------------
// Indices into the resolved R32 array (0=M73 … 15=M88), ordered top-to-bottom
// so each feeding pair stays vertically adjacent into the next round.
const LEFT_R32 = [1, 4, 0, 2, 10, 11, 8, 9];
const RIGHT_R32 = [3, 5, 6, 7, 13, 15, 12, 14];
const LEFT_R16 = [0, 1, 4, 5];
const RIGHT_R16 = [2, 3, 6, 7];
const LEFT_QF = [0, 1];
const RIGHT_QF = [2, 3];
const LEFT_SF = [0];
const RIGHT_SF = [1];

function Pill({ team, selected, onClick, disabled, dim, prob, decided }) {
  // prob is this team's win chance (0..1) for the matchup, or null if N/A.
  const pct = prob != null ? Math.round(prob * 100) : null;
  const barColor = selected ? "rgba(245,197,66,0.28)" : "rgba(63,131,219,0.22)";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        textAlign: "left",
        padding: "7px 10px",
        borderRadius: 7,
        border: `1px solid ${selected ? C.gold : C.line}`,
        background: team ? C.panel2 : "transparent",
        color: team ? (selected ? C.gold : C.text) : C.dim,
        cursor: disabled || !team ? "default" : "pointer",
        fontSize: 13,
        fontWeight: selected ? 700 : 500,
        opacity: dim ? 0.5 : 1,
        transition: "all .15s",
        fontFamily: "inherit",
        overflow: "hidden"
      }}
    >
      {pct != null && team && (
        <span
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${pct}%`,
            background: barColor,
            transition: "width .35s cubic-bezier(.4,0,.2,1)",
            zIndex: 0
          }}
        />
      )}
      <span style={{ position: "relative", zIndex: 1, fontSize: 15, width: 20 }}>
        {team ? FLAGS[team] || "" : ""}
      </span>
      <span
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {team || "—"}
      </span>
      {pct != null && team && (
        <span
          style={{
            position: "relative",
            zIndex: 1,
            fontSize: 11,
            fontWeight: 700,
            color: selected ? C.gold : C.dim,
            fontFamily: "'Oswald',sans-serif",
            minWidth: 30,
            textAlign: "right"
          }}
        >
          {decided && selected ? "✓" : `${pct}%`}
        </span>
      )}
    </button>
  );
}

function Match({ match, winners, onPick, label }) {
  const w = winners[match.id];
  const decided = !!w;
  let pHome = null;
  let pAway = null;
  if (match.home && match.away) {
    pHome = expScore(ELO[match.home] ?? 1500, ELO[match.away] ?? 1500);
    pAway = 1 - pHome;
  }
  return (
    <div style={{ marginBottom: 10 }}>
      {label && (
        <div
          style={{
            fontSize: 9,
            letterSpacing: 1,
            color: C.dim,
            marginBottom: 3,
            textTransform: "uppercase"
          }}
        >
          {label}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Pill
          team={match.home}
          selected={w === match.home}
          onClick={() => onPick(match.id, match.home)}
          disabled={!match.home}
          dim={w && w !== match.home}
          prob={pHome}
          decided={decided}
        />
        <Pill
          team={match.away}
          selected={w === match.away}
          onClick={() => onPick(match.id, match.away)}
          disabled={!match.away}
          dim={w && w !== match.away}
          prob={pAway}
          decided={decided}
        />
      </div>
    </div>
  );
}

function Column({ title, children, width = 185, align = "left" }) {
  return (
    <div style={{ minWidth: width, display: "flex", flexDirection: "column" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1.5,
          color: C.gold,
          marginBottom: 12,
          textTransform: "uppercase",
          textAlign: align
        }}
      >
        {title}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          flex: 1
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ maxWidth: 1400, margin: "0 auto 36px" }}>
      <div style={{ marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${C.line}` }}>
        <h2
          style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: 22,
            fontWeight: 600,
            margin: 0,
            letterSpacing: 0.3
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <div style={{ fontSize: 12.5, color: C.dim, marginTop: 3 }}>{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}

const POS_LABELS = { first: "1st", second: "2nd", third: "3rd" };
const POS_COLORS = { first: C.gold, second: "#9fb4d4", third: C.green };

function GroupCard({ g, teams, result, setPos, clearPos, probs }) {
  // pre-order teams by Elo so the card reads strongest-to-weakest
  const ordered = [...teams].sort((a, b) => (ELO[b] ?? 1500) - (ELO[a] ?? 1500));
  const [active, setActive] = useState(null); // which pos slot is being assigned
  const taken = team =>
    result.first === team
      ? "first"
      : result.second === team
      ? "second"
      : result.third === team
      ? "third"
      : null;

  // Smoother interaction:
  //  - assigning a team to a place it didn't hold, that vacated its *previous*
  //    place, immediately re-selects that vacated place (so you can refill it).
  //  - clicking a team that already holds a place (or the team in the active
  //    slot) unassigns it and selects that place.
  //  - clicking an unassigned team with no active selector does nothing.
  const onTeam = team => {
    const action = groupClickAction(taken(team), active, result);
    if (action.type === "assign") {
      setPos(g, action.pos, team);
      if (action.reselect) setActive(action.reselect);
    } else if (action.type === "clear") {
      clearPos(g, action.pos);
      setActive(action.pos);
    }
  };

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10
        }}
      >
        <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 17 }}>
          Group <span style={{ color: C.gold }}>{g}</span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {["first", "second", "third"].map(p => (
            <button
              key={p}
              onClick={() => setActive(active === p ? null : p)}
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "3px 7px",
                borderRadius: 6,
                border: `1px solid ${active === p ? POS_COLORS[p] : C.line}`,
                background: active === p ? POS_COLORS[p] + "22" : "transparent",
                color: active === p ? POS_COLORS[p] : C.dim,
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              {POS_LABELS[p]}
            </button>
          ))}
        </div>
      </div>
      {active && (
        <div style={{ fontSize: 10.5, color: C.dim, marginBottom: 8 }}>
          Tap a team to set as{" "}
          <b style={{ color: POS_COLORS[active] }}>{POS_LABELS[active]}</b>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {ordered.map(team => {
          const pos = taken(team);
          const adv = probs?.[team]?.advanceTop2 ?? null;
          const advPct = adv != null ? Math.round(adv * 100) : null;
          const actionable = !!(active || pos);
          return (
            <button
              key={team}
              onClick={() => onTeam(team)}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${pos ? POS_COLORS[pos] : C.line}`,
                background: C.panel2 + "00",
                color: C.text,
                cursor: actionable ? "pointer" : "default",
                fontSize: 13,
                fontWeight: pos ? 700 : 500,
                fontFamily: "inherit",
                textAlign: "left",
                overflow: "hidden"
              }}
            >
              {advPct != null && (
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${advPct}%`,
                    background: pos ? POS_COLORS[pos] + "26" : "rgba(63,131,219,0.16)",
                    zIndex: 0
                  }}
                />
              )}
              <span style={{ position: "relative", zIndex: 1, fontSize: 15, width: 20 }}>
                {FLAGS[team] || ""}
              </span>
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {team}
              </span>
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  fontSize: 10,
                  color: C.dim,
                  fontFamily: "'Oswald',sans-serif",
                  fontWeight: 600
                }}
                title="Elo rating"
              >
                {ELO[team] ?? "—"}
              </span>
              {advPct != null && (
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: 10.5,
                    color: pos ? POS_COLORS[pos] : C.dim,
                    fontFamily: "'Oswald',sans-serif",
                    fontWeight: 700,
                    minWidth: 30,
                    textAlign: "right"
                  }}
                  title="Chance to finish top 2"
                >
                  {advPct}%
                </span>
              )}
              {pos && (
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: 9.5,
                    fontWeight: 800,
                    color: POS_COLORS[pos],
                    fontFamily: "'Oswald',sans-serif"
                  }}
                >
                  {POS_LABELS[pos]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function WorldCupBracket({ initialState, initialNote = "" }) {
  const [groupResults, setGroupResults] = useState(initialState.groupResults);
  const [thirdsAdvancing, setThirdsAdvancing] = useState(initialState.thirdsAdvancing);
  const [winners, setWinners] = useState(initialState.winners);
  const [statusNote, setStatusNote] = useState(initialNote);

  // Persist on any change, and keep the URL hash in sync so the current bracket
  // is always shareable. Skip the very first run so we neither clobber a
  // freshly-loaded shared link nor dump the default forecast into the URL.
  const firstRun = useRef(true);
  useEffect(() => {
    const state = { groupResults, thirdsAdvancing, winners };
    saveState(state);
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const empty =
      Object.keys(groupResults).length === 0 &&
      thirdsAdvancing.length === 0 &&
      Object.keys(winners).length === 0;
    try {
      const base = location.pathname + location.search;
      history.replaceState(null, "", empty ? base : base + buildHash(state));
    } catch {
      /* history unavailable — ignore */
    }
  }, [groupResults, thirdsAdvancing, winners]);

  const setGroupPos = (g, pos, team) => {
    setGroupResults(prev => {
      const cur = { ...(prev[g] || {}) };
      // if team already holds another pos in this group, clear it
      for (const k of ["first", "second", "third"]) {
        if (cur[k] === team && k !== pos) delete cur[k];
      }
      cur[pos] = team;
      return { ...prev, [g]: cur };
    });
    setWinners({}); // reset knockout picks since seeding changed
    setStatusNote("Your picks — saved automatically");
  };

  const clearGroupPos = (g, pos) => {
    setGroupResults(prev => {
      const cur = { ...(prev[g] || {}) };
      delete cur[pos];
      return { ...prev, [g]: cur };
    });
    // dropping a group's 3rd means it can no longer be an advancing third
    if (pos === "third") setThirdsAdvancing(prev => prev.filter(x => x !== g));
    setWinners({});
    setStatusNote("Your picks — saved automatically");
  };

  const toggleThird = g => {
    setThirdsAdvancing(prev => {
      if (prev.includes(g)) return prev.filter(x => x !== g);
      if (prev.length >= 8) return prev; // cap at 8
      return [...prev, g];
    });
    setWinners({});
    setStatusNote("Your picks — saved automatically");
  };

  const allGroupsComplete = GROUP_KEYS.every(
    g => groupResults[g]?.first && groupResults[g]?.second && groupResults[g]?.third
  );
  const thirdsComplete = thirdsAdvancing.length === 8;

  const thirdAssignment = useMemo(() => {
    if (thirdsAdvancing.length !== 8) return null;
    return assignThirds(thirdsAdvancing);
  }, [thirdsAdvancing]);

  // Group advance probabilities (Monte Carlo), computed once — static per group.
  const groupProbs = useMemo(() => {
    const out = {};
    for (const g of GROUP_KEYS) out[g] = simulateGroupProbs(GROUPS[g]);
    return out;
  }, []);

  const { r32, r16, qf, sf, final, champion } = useMemo(
    () => buildBracket(groupResults, thirdAssignment, winners),
    [groupResults, thirdAssignment, winners]
  );

  const pickWinner = (matchId, team) => {
    if (!team) return;
    setWinners(prev => pruneDownstream({ ...prev, [matchId]: team }, groupResults, thirdAssignment));
    setStatusNote("Your picks — saved automatically");
  };

  const prepopulateFromElo = () => {
    const f = computeEloForecast();
    setGroupResults(f.groupResults);
    setThirdsAdvancing(f.thirdsAdvancing);
    setWinners(f.winners);
    setStatusNote("Prefilled from Elo forecast");
  };

  const reset = () => {
    clearSaved();
    setGroupResults({});
    setThirdsAdvancing([]);
    setWinners({});
    setStatusNote("Cleared — pick manually or forecast from Elo");
  };

  const copyLink = () => {
    const state = { groupResults, thirdsAdvancing, winners };
    const url = location.origin + location.pathname + location.search + buildHash(state);
    const done = () => setStatusNote("Link copied — share it to compare picks");
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(done, () => {
          history.replaceState(null, "", url);
          setStatusNote("Couldn't copy — URL updated, copy it from the address bar");
        });
      } else {
        history.replaceState(null, "", url);
        setStatusNote("URL updated — copy it from the address bar to share");
      }
    } catch {
      setStatusNote("Sharing unavailable in this context");
    }
  };

  // Render one bracket column from a list of R32-indices (or pre-built matches).
  const matchColumn = (title, items, { labelOffset, align } = {}) => (
    <Column title={title} align={align}>
      {items.map(({ m, idx }) => (
        <Match
          key={m.id}
          match={m}
          winners={winners}
          onPick={pickWinner}
          label={labelOffset != null ? `Match ${idx + 1}` : undefined}
        />
      ))}
    </Column>
  );

  const pickR32 = indices => indices.map(idx => ({ m: r32[idx], idx }));
  const pickRound = (round, indices) => indices.map(idx => ({ m: round[idx], idx }));

  return (
    <div
      style={{
        background: C.bg,
        minHeight: "100vh",
        color: C.text,
        fontFamily: "'Inter', system-ui, sans-serif",
        padding: "24px 20px 80px"
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;800&family=Oswald:wght@500;600;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { height: 10px; width: 10px; }
        ::-webkit-scrollbar-thumb { background: ${C.line}; border-radius: 5px; }
      `}</style>

      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto 28px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 4,
              color: C.gold,
              fontWeight: 700,
              fontFamily: "'Oswald', sans-serif"
            }}
          >
            CANADA · MEXICO · USA
          </div>
          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: 42,
              fontWeight: 700,
              margin: "2px 0 0",
              letterSpacing: -0.5
            }}
          >
            WORLD CUP 2026 <span style={{ color: C.gold }}>BRACKET</span>
          </h1>
          {statusNote && (
            <div
              style={{
                fontSize: 11.5,
                color: C.dim,
                marginTop: 6,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: C.green,
                  display: "inline-block"
                }}
              />
              {statusNote}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={prepopulateFromElo}
            style={{
              background: C.gold,
              border: `1px solid ${C.gold}`,
              color: "#0a1628",
              padding: "9px 18px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: 800,
              fontFamily: "inherit"
            }}
          >
            ⚡ Forecast from Elo
          </button>
          <button
            onClick={copyLink}
            style={{
              background: "transparent",
              border: `1px solid ${C.gold}`,
              color: C.gold,
              padding: "8px 16px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit"
            }}
          >
            🔗 Copy link
          </button>
          <button
            onClick={reset}
            style={{
              background: "transparent",
              border: `1px solid ${C.line}`,
              color: C.dim,
              padding: "8px 16px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "inherit"
            }}
          >
            Reset all
          </button>
        </div>
      </div>

      {champion && (
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto 28px",
            textAlign: "center",
            padding: "20px",
            borderRadius: 14,
            background: "linear-gradient(135deg, rgba(245,197,66,0.18), rgba(245,197,66,0.04))",
            border: `1px solid ${C.gold}`
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 3, color: C.gold, fontWeight: 700 }}>
            🏆 YOUR CHAMPION
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 36, fontWeight: 700, marginTop: 4 }}>
            {FLAGS[champion]} {champion}
          </div>
        </div>
      )}

      <Section
        title="1 — Pick group finishers"
        subtitle="Choose 1st, 2nd, and 3rd per group — or hit “Forecast from Elo” to auto-fill. Numbers show each team's Elo rating."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16
          }}
        >
          {GROUP_KEYS.map(g => (
            <GroupCard
              key={g}
              g={g}
              teams={GROUPS[g]}
              result={groupResults[g] || {}}
              setPos={setGroupPos}
              clearPos={clearGroupPos}
              probs={groupProbs[g]}
            />
          ))}
        </div>
      </Section>

      <Section
        title="2 — Pick the 8 best third-place teams"
        subtitle={`8 of 12 third-placed teams advance. Selected ${thirdsAdvancing.length}/8.`}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
            gap: 10
          }}
        >
          {GROUP_KEYS.map(g => {
            const third = groupResults[g]?.third;
            const on = thirdsAdvancing.includes(g);
            const atCap = thirdsAdvancing.length >= 8 && !on;
            return (
              <button
                key={g}
                onClick={() => third && toggleThird(g)}
                disabled={!third || atCap}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 9,
                  border: `1px solid ${on ? C.green : C.line}`,
                  background: on ? "rgba(52,211,153,0.12)" : C.panel,
                  color: third ? C.text : C.dim,
                  cursor: !third || atCap ? "default" : "pointer",
                  opacity: !third || atCap ? 0.5 : 1,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "inherit"
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: on ? C.green : C.dim,
                    fontFamily: "'Oswald',sans-serif"
                  }}
                >
                  {g}3
                </span>
                <span style={{ fontSize: 15 }}>{third ? FLAGS[third] : ""}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {third || "set 3rd first"}
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title="3 — Knockout bracket"
        subtitle="Two-sided bracket — left and right halves meet at the Final. Bars show each team's Elo win probability; pick a winner and the next round recomputes."
      >
        {!allGroupsComplete || !thirdsComplete ? (
          <div
            style={{
              padding: "30px",
              textAlign: "center",
              color: C.dim,
              border: `1px dashed ${C.line}`,
              borderRadius: 12,
              fontSize: 14
            }}
          >
            {!allGroupsComplete
              ? "Finish picking 1st, 2nd & 3rd for all 12 groups to unlock the bracket."
              : `Select all 8 third-place teams to unlock the bracket (${thirdsAdvancing.length}/8 chosen).`}
          </div>
        ) : !thirdAssignment ? (
          <div
            style={{
              padding: "30px",
              textAlign: "center",
              color: "#f0a868",
              border: `1px dashed #6b4a2a`,
              borderRadius: 12,
              fontSize: 14
            }}
          >
            This particular set of 8 third-place groups can't be slotted under FIFA's placement rules — no
            valid arrangement keeps every team out of its own group winner's match. Swap one of the eight
            (groups {thirdsAdvancing.join(", ")}) and the bracket will resolve.
          </div>
        ) : (
          <div style={{ overflowX: "auto", paddingBottom: 16 }}>
            <div style={{ display: "flex", gap: 14, minWidth: 1500, alignItems: "stretch" }}>
              {matchColumn("Round of 32", pickR32(LEFT_R32), { labelOffset: true, align: "left" })}
              {matchColumn("Round of 16", pickRound(r16, LEFT_R16), { align: "left" })}
              {matchColumn("Quarterfinals", pickRound(qf, LEFT_QF), { align: "left" })}
              {matchColumn("Semifinal", pickRound(sf, LEFT_SF), { align: "left" })}

              <Column title="Final" width={205} align="center">
                <div style={{ marginTop: "auto", marginBottom: "auto" }}>
                  <Match match={final[0]} winners={winners} onPick={pickWinner} />
                  {champion && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "12px",
                        borderRadius: 10,
                        background: "rgba(245,197,66,0.1)",
                        border: `1px solid ${C.gold}`,
                        textAlign: "center"
                      }}
                    >
                      <div style={{ fontSize: 9, letterSpacing: 2, color: C.gold }}>CHAMPION</div>
                      <div style={{ fontWeight: 800, marginTop: 2 }}>
                        {FLAGS[champion]} {champion}
                      </div>
                    </div>
                  )}
                </div>
              </Column>

              {matchColumn("Semifinal", pickRound(sf, RIGHT_SF), { align: "right" })}
              {matchColumn("Quarterfinals", pickRound(qf, RIGHT_QF), { align: "right" })}
              {matchColumn("Round of 16", pickRound(r16, RIGHT_R16), { align: "right" })}
              {matchColumn("Round of 32", pickR32(RIGHT_R32), { labelOffset: true, align: "right" })}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
